/**
 * @fileoverview Sticky element observer utility for managing sticky positioning state.
 * 
 * This module provides functionality to observe when elements with CSS `position: sticky`
 * become "stuck" to their container. It adds a 'stuck' class when the element reaches
 * its sticky threshold, enabling custom styling for the stuck state. The observer uses
 * IntersectionObserver for performance optimization, only adding scroll listeners when
 * the element is visible in the viewport.
 * 
 * @module helpers/sticky-observer
 */

/**
 * Creates an observer that tracks when a sticky-positioned element becomes "stuck".
 * 
 * This function sets up an IntersectionObserver to monitor element visibility and
 * adds scroll event listeners only when the element is in view. When the element
 * reaches its sticky threshold (defined by its CSS `top` property), it adds a
 * 'stuck' class to enable custom styling.
 * 
 * @param {HTMLElement} element - The element to observe for sticky state changes
 * @returns {() => void} A cleanup function that removes all observers and event listeners
 * 
 * @example
 * // Basic usage with a sticky header
 * const header = document.querySelector('.site-header');
 * const cleanup = createStickyObserver(header);
 * 
 * // CSS for the sticky element
 * // .site-header {
 * //   position: sticky;
 * //   top: 0;
 * //   transition: all 0.3s ease;
 * // }
 * // .site-header.stuck {
 * //   background-color: rgba(255, 255, 255, 0.95);
 * //   box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
 * // }
 * 
 * @example
 * // Using with multiple sticky elements
 * const stickyElements = document.querySelectorAll('.sticky-element');
 * const cleanupFunctions = [];
 * 
 * stickyElements.forEach(element => {
 *   cleanupFunctions.push(createStickyObserver(element));
 * });
 * 
 * // Clean up all observers when done
 * window.addEventListener('beforeunload', () => {
 *   cleanupFunctions.forEach(cleanup => cleanup());
 * });
 * 
 * @example
 * // Using with a sticky sidebar that sticks 20px from top
 * const sidebar = document.querySelector('.sidebar');
 * // CSS: .sidebar { position: sticky; top: 20px; }
 * const cleanup = createStickyObserver(sidebar);
 * 
 * // The 'stuck' class will be added when sidebar reaches 20px from top
 */
function createStickyObserver(element: HTMLElement) {
  let stickyTriggered = false;
  const originalTopPosition = element.getBoundingClientRect().top + window.scrollY;
  const style = window.getComputedStyle(element);

  // Create an observer for the element
  const observer = new IntersectionObserver((entries) => {
    const entry = entries[0];

    if (entry.isIntersecting) {
      // Element is visible, start listening for scroll events
      window.addEventListener('scroll', checkStickyState);
    } else {
      // Element is not visible, remove scroll listener
      window.removeEventListener('scroll', checkStickyState);
      element.classList.remove('stuck');
      stickyTriggered = false;
    }
  }, { threshold: 0 });

  /**
   * Checks if the element has reached its sticky threshold and updates the 'stuck' class accordingly.
   * This function is called on scroll events when the element is visible in the viewport.
   */
  function checkStickyState() {
    try {
      const STICKY_THRESHOLD = parseFloat(style.getPropertyValue('top'));
      const scrollY = window.scrollY;

      // Validate scroll position and threshold values
      if (typeof scrollY !== 'number' || !isFinite(scrollY)) {
        console.warn('Invalid scroll position in sticky observer:', scrollY);
        return;
      }

      if (isNaN(STICKY_THRESHOLD)) {
        console.warn('Invalid sticky threshold in sticky observer:', STICKY_THRESHOLD);
        return;
      }

      if (!stickyTriggered && scrollY + STICKY_THRESHOLD >= originalTopPosition) {
        element.classList.add('stuck');
        stickyTriggered = true;
      } else if (stickyTriggered && scrollY + STICKY_THRESHOLD < originalTopPosition) {
        element.classList.remove('stuck');
        stickyTriggered = false;
      }
    } catch (error) {
      console.error('Error in sticky observer checkStickyState:', error);
    }
  }

  observer.observe(element);

  // Clean up function
  return () => {
    observer.disconnect();
    window.removeEventListener('scroll', checkStickyState);
  };
}

export default createStickyObserver;
